import { useState } from 'react';

// Mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

// Local imports
import Where from '../../Where.js';

// Change one contract line's origin, destination or product without deleting it and losing its
// prices. The charges move onto the new product as they are; nothing is saved until the
// contract itself is saved.

function productLabel(p) {
    const type = (p.type === 'LCL' || p.type === 'By Volume or Weight (sea)') ? 'LCL' : p.type;
    return `${type}${p.dangerous ? ' (Dangerous)' : ''}`;
}

function OpportunityContractEditLineModal({ open, line, products, transportationMethod, seaPorts, unlocode, onDone, onClose }) {
    const theme = useTheme();
    const route = line?.routes?.[0] || {};
    const [ source, setSource ] = useState(route.source || null);
    const [ destination, setDestination ] = useState(route.destination || null);
    const [ productId, setProductId ] = useState(line?.product?.id || '');

    const product = products.find(p => p.id === productId) || null;
    const unchanged = source === route.source && destination === route.destination && productId === line?.product?.id;

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Edit contract line</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                    Change where this line goes or what it carries. Its prices stay as they are.
                </Typography>
                <Box sx={{ padding: '12px 18px', background: theme.palette.panel1, border: `1px solid ${theme.palette.border1}`, borderRadius: '6px' }}>
                    <Where
                        transportationMethod={transportationMethod}
                        errors={[]}
                        source={source}
                        destination={destination}
                        setSource={v => setSource(v)}
                        setDestination={v => setDestination(v)}
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                    />
                </Box>
                <TextField select size='small' label='Product' value={productId} onChange={e => setProductId(e.target.value)} sx={{ maxWidth: 360 }}>
                    {products.map(p => <MenuItem key={p.id} value={p.id}>{productLabel(p)}</MenuItem>)}
                </TextField>
                {line?.hasCosts && product && line?.product && product.type !== line.product.type && (
                    <Alert severity='warning'>
                        This line is priced from supplier costs for {productLabel(line.product)}. Those costs don't apply to a {product.type}, so charges based on them will show 0 until you link costs for the new product. Fixed prices move over as they are.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant='contained' disabled={!product || unchanged} onClick={() => onDone({ source, destination, product })}>
                    Apply to this line
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default OpportunityContractEditLineModal;
